import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Phone, MessageCircle } from "lucide-react";
import { LogoLockup } from "./Logo";
import { SERVICES } from "../data/services";
import { LOCATIONS } from "../data/locations";
import { PHONE_TEL, PHONE_DISPLAY, waLink, DEFAULT_WA_MSG } from "../data/site";

function Dropdown({ label, items, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        data-testid={testid}
        className="flex items-center gap-1.5 font-telemetry text-xs uppercase tracking-[0.2em] text-zinc-300 hover:text-white transition-colors py-4"
        onClick={() => setOpen((o) => !o)}
      >
        {label}
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 pt-2 z-50"
          >
            <div className="w-64 bg-[#131316] border border-[#27272A] rounded-sm overflow-hidden">
              {items.map((item) => (
                <Link
                  key={item.slug}
                  to={item.to}
                  className="block px-5 py-3 text-sm text-zinc-300 hover:text-white hover:bg-[#1A1A1E] border-b border-[#27272A] last:border-0 transition-colors"
                >
                  <span className="font-display font-bold uppercase tracking-wide text-base">
                    {item.label}
                  </span>
                  {item.meta && (
                    <span className="block font-telemetry text-[10px] text-[#FF5500] tracking-widest uppercase mt-0.5">
                      {item.meta}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);

  const serviceItems = SERVICES.map((s) => ({
    slug: s.slug,
    to: `/services/${s.slug}`,
    label: s.shortName,
    meta: "Mobile · 24/7",
  }));
  const locationItems = LOCATIONS.map((l) => ({
    slug: l.slug,
    to: `/areas/${l.slug}`,
    label: l.name,
    meta: l.eta + " response",
  }));

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <div className="bg-[#0B0B0C]/85 backdrop-blur-md border-b border-[#27272A]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-16 sm:h-20">
          <Link to="/" data-testid="header-logo-link" className="shrink-0">
            <LogoLockup />
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            <Dropdown label="Services" items={serviceItems} testid="header-services-dropdown" />
            <Dropdown label="Areas" items={locationItems} testid="header-areas-dropdown" />
            <Link
              to="/contact"
              data-testid="header-contact-link"
              className="font-telemetry text-xs uppercase tracking-[0.2em] text-zinc-300 hover:text-white transition-colors py-4"
            >
              Book / Contact
            </Link>
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
              href={waLink(DEFAULT_WA_MSG)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-whatsapp-button"
              className="btn-glow flex items-center gap-2 px-5 py-2.5 rounded-full border border-emerald-500/60 text-emerald-400 hover:bg-emerald-500 hover:text-black font-telemetry text-xs uppercase tracking-[0.15em]"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
            <a
              href={PHONE_TEL}
              data-testid="header-call-button"
              className="btn-glow flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF5500] hover:bg-[#FF6A1A] text-black font-telemetry text-xs font-semibold uppercase tracking-[0.15em]"
            >
              <Phone className="w-4 h-4" /> Call {PHONE_DISPLAY}
            </a>
          </div>

          <button
            data-testid="header-mobile-menu-button"
            className="md:hidden flex flex-col justify-center items-center w-11 h-11 border border-[#27272A] rounded-sm gap-[5px]"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Menu"
          >
            <span className={`block w-5 h-[2px] bg-white transition-transform duration-300 ${menuOpen ? "rotate-45 translate-y-[7px]" : ""}`} />
            <span className={`block w-5 h-[2px] bg-white transition-opacity duration-300 ${menuOpen ? "opacity-0" : ""}`} />
            <span className={`block w-5 h-[2px] bg-white transition-transform duration-300 ${menuOpen ? "-rotate-45 -translate-y-[7px]" : ""}`} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="md:hidden absolute top-full inset-x-0 bg-[#0B0B0C]/97 backdrop-blur-xl border-b border-[#27272A] max-h-[calc(100vh-4rem)] overflow-y-auto"
          >
            <div className="px-5 py-6 space-y-6">
              <div>
                <p className="font-telemetry text-[10px] uppercase tracking-[0.3em] text-[#FF5500] mb-3">
                  Services
                </p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  {SERVICES.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/services/${s.slug}`}
                      onClick={() => setMenuOpen(false)}
                      data-testid={`mobile-nav-service-${s.slug}`}
                      className="font-display font-bold uppercase text-lg text-zinc-200 hover:text-[#FF5500] transition-colors py-1"
                    >
                      {s.shortName}
                    </Link>
                  ))}
                </div>
              </div>
              <div>
                <p className="font-telemetry text-[10px] uppercase tracking-[0.3em] text-[#FF5500] mb-3">
                  Areas we cover
                </p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  {LOCATIONS.map((l) => (
                    <Link
                      key={l.slug}
                      to={`/areas/${l.slug}`}
                      onClick={() => setMenuOpen(false)}
                      data-testid={`mobile-nav-location-${l.slug}`}
                      className="font-display font-bold uppercase text-lg text-zinc-200 hover:text-[#FF5500] transition-colors py-1"
                    >
                      {l.name}
                    </Link>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 pt-2">
                <a
                  href={PHONE_TEL}
                  data-testid="mobile-menu-call-button"
                  className="btn-glow flex items-center justify-center gap-2 py-3.5 rounded-full bg-[#FF5500] text-black font-telemetry text-xs font-semibold uppercase tracking-[0.15em]"
                  onClick={() => setMenuOpen(false)}
                >
                  <Phone className="w-4 h-4" /> Call now
                </a>
                <a
                  href={waLink(DEFAULT_WA_MSG)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-menu-whatsapp-button"
                  className="btn-glow flex items-center justify-center gap-2 py-3.5 rounded-full border border-emerald-500/60 text-emerald-400 font-telemetry text-xs uppercase tracking-[0.15em]"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
